import { ProductPageSectionParams, ProductPageSectionType } from "@/backend/access/productPageSection/types";
import { ProductPageContent } from "@/backend/models/productPageContent";
import { ProductPageSection } from "@/backend/models/productPageSection";
import { CreateProductPageContentRequest } from "@/types/productPageContentTypes";
import { create } from "zustand";
import { ProductPageSectionController } from "../controllers/productPageSectionController";
import { ProductPageContentController } from "../controllers/productPageContentController";
import { ProductPageContentParams } from "@/backend/access/productPageContent/types";
import { CreateProductPageSectionRequest } from "@/types/productPageSectionTypes";

type ProductPageStore = {
  sections: ProductPageSection[];
  contents: ProductPageContent[];
  reset: () => void;
  fetchSections: (procedureId: string, type: ProductPageSectionType) => Promise<void>;
  createSection: (params: CreateProductPageSectionRequest) => Promise<void>;
  updateSection: (section: ProductPageSectionParams) => Promise<void>;
  duplicateSection: (section: ProductPageSection) => Promise<void>;
  orderSections: (sections: ProductPageSection[]) => Promise<void>;
  deleteSection: (section: ProductPageSection) => Promise<void>;
  fetchContents: (procedureInstanceId: string, type: ProductPageSectionType) => Promise<void>;
  fetchContent: (contentId: string) => Promise<void>;
  fetchContentsBySection: (productPageSectionId: string) => Promise<void>;
  createContent: (params: CreateProductPageContentRequest) => Promise<void>;
  updateContent: (content: ProductPageContentParams) => Promise<void>;
  duplicateContent: (content: ProductPageContent) => Promise<void>;
  orderContents: (contents: ProductPageContent[]) => Promise<void>;
  deleteContent: (content: ProductPageContent) => Promise<void>;
};

const initialState = {
  sections: [],
  contents: [],
};

export const useProductPageStore = create<ProductPageStore>((set, get) => ({
  ...initialState,

  reset: () => {
    set(initialState);
  },

  fetchSections: async (procedureId, type) => {
    const response = await new ProductPageSectionController().getAllByProcedureInstance(procedureId, type);
    if (response.errors) return;
    const sections = response.productPageSections.map((s) => new ProductPageSection(s));
    set({ sections });
  },

  createSection: async (params) => {
    const response = await new ProductPageSectionController().createProductPageSection(params);
    if (response.errors || !response.productPageSection) return;
    await get().fetchSections(params.procedureInstanceId, params.type);
  },

  updateSection: async (sectionParams) => {
    const response = await new ProductPageSectionController().updateProductPageSection(sectionParams);
    if (response.errors || !response.productPageSection) return;
    const updated = new ProductPageSection(response.productPageSection);
    set({
      sections: get().sections.map((s) => (s.id === updated.id ? updated : s)),
    });
  },

  duplicateSection: async (section) => {
    const response = await new ProductPageSectionController().duplicateProductPageSection(section.id);
    if (response.errors || !response.productPageSection) return;
    await get().fetchSections(section.procedureInstanceId, section.type);
    await get().fetchContentsBySection(response.productPageSection.id);
  },

  orderSections: async (sections) => {
    const response = await new ProductPageSectionController().orderProductPageSections(sections);
    if (response.errors) return;
    set({ sections: response.productPageSections.map((s) => new ProductPageSection(s)) });
  },

  deleteSection: async (section) => {
    const response = await new ProductPageSectionController().deleteProductPageSection(section.id);
    if (response.errors) return;
    await get().fetchSections(section.procedureInstanceId, section.type);
    set({
      contents: get().contents.filter((c) => c.productPageSectionId !== section.id),
    });
  },

  fetchContents: async (procedureId, type) => {
    const response = await new ProductPageContentController().getAllByProcedureInstance(procedureId, type);
    if (response.errors) return;
    const contents = response.productPageContents.map((c) => new ProductPageContent(c));
    set({ contents });
  },

  fetchContent: async (contentId) => {
    const response = await new ProductPageContentController().read(contentId);
    if (response.errors || !response.productPageContent) return;
    const fetched = new ProductPageContent(response.productPageContent);
    set({ contents: get().contents.map((c) => (c.id === fetched.id ? fetched : c)) });
  },

  fetchContentsBySection: async (productPageSectionId) => {
    const response = await new ProductPageContentController().getAllContentsBySection(productPageSectionId);
    if (response.errors) return;
    const refreshed = response.productPageContents.map((c) => new ProductPageContent(c));
    const otherContents = get().contents.filter((c) => c.productPageSectionId !== productPageSectionId);
    set({ contents: [...otherContents, ...refreshed] });
  },

  createContent: async (params) => {
    const response = await new ProductPageContentController().createProductPageContent(params);
    if (response.errors || !response.productPageContent) return;
    await get().fetchContentsBySection(params.productPageSectionId);
  },

  updateContent: async (req) => {
    const response = await new ProductPageContentController().updateProductPageContent(req);
    if (response.errors || !response.productPageContent) return;
    const updated = new ProductPageContent(response.productPageContent);
    set({
      contents: get().contents.map((c) => (c.id === updated.id ? updated : c)),
    });
  },

  duplicateContent: async (content) => {
    const response = await new ProductPageContentController().duplicateProductPageContent(content.id);
    if (response.errors || !response.productPageContent) return;
    await get().fetchContentsBySection(content.productPageSectionId);
  },

  orderContents: async (contents) => {
    const response = await new ProductPageContentController().orderProductPageContents(contents);
    if (response.errors) return;
    const otherContents = get().contents.filter((c) => !contents.some((oc) => oc.id === c.id));
    const refreshed = response.productPageContents.map((c) => new ProductPageContent(c));
    set({ contents: [...otherContents, ...refreshed] });
  },

  deleteContent: async (content) => {
    const response = await new ProductPageContentController().deleteProductPageContent(content.id);
    if (response.errors) return;
    set({
      contents: get().contents.filter((c) => c.id !== content.id),
    });
    await get().fetchContentsBySection(content.productPageSectionId);
  },
}));
